import { useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlass, ChartBar, FileText, Kanban, Lightning,
  UserCircle, CaretRight, Bell, Globe, UserSwitch, BookOpen,
  Buildings, Pulse, SignOut
} from "@phosphor-icons/react";

const mainNav = [
  { id: "dashboard", label: "Command Center", icon: ChartBar    },
  { id: "search",    label: "Search Jobs",    icon: MagnifyingGlass },
  { id: "results",   label: "Results",        icon: Lightning   },
  { id: "watchlist", label: "Watchlist",      icon: Buildings   },
  { id: "pipeline",  label: "Pipeline",       icon: Kanban      },
];

const toolsNav = [
  { id: "radar",     label: "Job Radar",  icon: Bell        },
  { id: "generator", label: "Generator",  icon: Globe       },
  { id: "resume",    label: "Resume",     icon: UserCircle  },
  { id: "guide",     label: "Guide",      icon: BookOpen    },
];

// systemNav removed — Settings and Telemetry are admin-only (live in AdminPanel)

const colorClassMap = {
  amber:   { bg: "bg-amber-500",   text: "text-amber-500",   dim: "bg-amber-500/15"   },
  emerald: { bg: "bg-emerald-500", text: "text-emerald-500", dim: "bg-emerald-500/15" },
  sky:     { bg: "bg-sky-400",     text: "text-sky-400",     dim: "bg-sky-400/15"     },
  purple:  { bg: "bg-purple-500",  text: "text-purple-500",  dim: "bg-purple-500/15"  },
};

export default function Sidebar({ view, setView, onSwitchPersona }) {
  const { profile, stats, unreadAlerts, fetchAlertCount, activePersona, isAdmin, logout } = useApp();
  const navigate = useNavigate();
  const c = colorClassMap[activePersona?.color] || colorClassMap.amber;

  const handleLogout = async () => {
    await logout();
    navigate('/auth', { replace: true });
  };

  useEffect(() => {
    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 30000);
    return () => clearInterval(interval);
  }, [fetchAlertCount]);

  return (
    <div className="jp-sidebar" data-testid="sidebar">
      {/* Brand */}
      <div className="p-5 pb-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-amber-500 flex items-center justify-center">
            <Lightning size={18} weight="fill" className="text-black" />
          </div>
          <div>
            <div className="font-['Outfit'] text-base font-semibold text-white tracking-tight" data-testid="brand-logo">
              sankhya
            </div>
            <div className="text-[10px] text-zinc-600 tracking-wider">AI SEARCH ENGINE</div>
          </div>
        </div>
      </div>

      {/* Active Persona Strip */}
      <div className="mx-3 mb-3" data-testid="active-persona-strip">
        <button
          type="button"
          onClick={onSwitchPersona}
          data-testid="switch-persona-btn"
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md border border-white/8 hover:border-white/15 transition-colors group ${activePersona ? c.dim : 'bg-white/[0.02]'}`}
        >
          {activePersona ? (
            <>
              <div className={`w-6 h-6 rounded-full ${c.bg} flex items-center justify-center shrink-0`}>
                <span className="text-black text-[11px] font-bold font-['Outfit']">
                  {activePersona.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className={`text-xs font-medium ${c.text} truncate`}>{activePersona.name}</div>
                {activePersona.role_title && (
                  <div className="text-[10px] text-zinc-600 truncate">{activePersona.role_title}</div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center shrink-0">
                <UserCircle size={14} className="text-zinc-500" />
              </div>
              <div className="text-xs text-zinc-600 flex-1 text-left">No persona selected</div>
            </>
          )}
          <UserSwitch size={14} className="text-zinc-600 group-hover:text-zinc-400 transition-colors shrink-0" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-1 overflow-y-auto" data-testid="sidebar-nav">
        <div className="jp-label px-3 mb-2">CORE</div>
        {mainNav.map(item => {
          const isActive = view === item.id;
          return (
            <button
              key={item.id}
              type="button"
              data-testid={`nav-${item.id}`}
              className={`jp-nav-item rounded-md mb-0.5 w-full text-left ${isActive ? "active" : ""}`}
              onClick={() => setView(item.id)}
            >
              <item.icon size={18} weight={isActive ? "fill" : "regular"} />
              <span className="text-sm flex-1">{item.label}</span>
              {isActive && <CaretRight size={14} className="ml-auto opacity-60" />}
            </button>
          );
        })}

        <div className="jp-label px-3 mb-2 mt-3">TOOLS</div>
        {toolsNav.map(item => {
          const isRadar = item.id === "radar";
          const isActive = view === item.id;
          return (
            <button
              key={item.id}
              type="button"
              data-testid={`nav-${item.id}`}
              className={`jp-nav-item rounded-md mb-0.5 w-full text-left ${isActive ? "active" : ""}`}
              onClick={() => setView(item.id)}
            >
              <item.icon size={18} weight={isActive ? "fill" : "regular"} />
              <span className="text-sm flex-1">{item.label}</span>
              {isRadar && unreadAlerts > 0 && (
                <span className="ml-auto bg-amber-500 text-black text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center font-mono">
                  {unreadAlerts > 9 ? "9+" : unreadAlerts}
                </span>
              )}
              {isActive && !isRadar && <CaretRight size={14} className="ml-auto opacity-60" />}
            </button>
          );
        })}

        {isAdmin && (
          <>
            <div className="jp-label px-3 mb-2 mt-3">ADMIN</div>
            <a
              href="/admin"
              data-testid="nav-admin"
              className="jp-nav-item rounded-md mb-0.5 w-full text-left flex items-center gap-2 px-3 py-2 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <Pulse size={18} weight="regular" />
              <span className="text-sm flex-1">Admin Forge</span>
            </a>
          </>
        )}
      </nav>

      {/* Status */}
      <div className="border-t border-white/5 p-5" data-testid="sidebar-status">
        <div className="jp-label mb-3">SYSTEM STATUS</div>
        <div className="space-y-2">
          <div className="flex items-center gap-2.5">
            <div className={`w-2 h-2 rounded-full ${profile ? "bg-emerald-500" : "bg-red-500"}`} />
            <span className="text-xs text-zinc-500">
              {profile ? (profile.name || "Resume loaded") : "No resume uploaded"}
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-xs text-zinc-500">{stats?.total_jobs || 0} jobs indexed</span>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-xs text-zinc-500">API operational</span>
          </div>
        </div>
      </div>

      {/* Sign Out */}
      <div className="px-3 pb-5">
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all group"
        >
          <SignOut size={18} weight="regular" className="group-hover:rotate-12 transition-transform" />
          <span className="text-sm font-medium">Sign Out</span>
        </button>
      </div>
    </div>
  );
}
